import React from "react";
import { Table } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import type { TableProps } from "rc-table/lib/Table";

import { columnMapper, IColumnData, isEmpty } from "@Utils";
import LargeSpinner from "@Components/shared/LargeSpinner";
import { Refresh } from "@mui/icons-material";
import IconButton from "./IconButton";

type TableInterface = Parameters<typeof Table>[0];

interface IMenuOptions {
    onExport?: () => Promise<boolean>;
    onReload?: () => void;
}

export interface IDynamicTableProps<T> extends Omit<TableInterface, "dataSource"> {
    columnData: IColumnData<T>[];
    dataSource: TableProps<T>["data"];
    isFetching?: boolean;
    isLoading: boolean;
    menuOptions?: IMenuOptions;
}

function DynamicTable<T extends object>({
    columnData,
    dataSource,
    isFetching,
    isLoading,
    menuOptions,
    pagination,

    title,
    ...otherProps
}: React.PropsWithChildren<IDynamicTableProps<T>>) {
    if (isLoading) {
        return <LargeSpinner />;
    } else {
        const hasTitle = !!title;
        const { onExport, onReload } = menuOptions ?? {};
        const hasMenu = !isEmpty(menuOptions ?? {});
        const columns = dataSource?.length ? columnMapper(columnData) : [];

        const classNames = [otherProps.className || "newUI-glass", "dynamic-table"];
        if (otherProps?.onRow) {
            classNames.push("row-with-actions");
        }

        if (!hasTitle && hasMenu) {
            classNames.push("no-title");
        }

        /**
         * We have added a menu that holds several buttons / actions
         * We are using the title element because it's an easy way to add and position our menu directly inside of the table
         * If there is no title property we hide the title header but the menu remains visible if present
         */
        const fullTitle =
            !!hasTitle || !!hasMenu
                ? (params: readonly object[]) => (
                      <>
                          {title?.(params)}
                          <div className="title-menu">
                              {!!onExport ? (
                                  <span className="menu-button">
                                      <DownloadOutlined onClick={onExport} disabled={isFetching} />
                                  </span>
                              ) : null}
                              {!!onReload ? (
                                  <IconButton
                                      className="secondary flex"
                                      onClick={onReload}
                                      icon={<Refresh />}
                                      loading={isFetching}
                                      style={{ marginTop: "-20px" }}
                                  >
                                      Refresh
                                  </IconButton>
                              ) : null}
                          </div>
                      </>
                  )
                : null;

        return (
            <Table
                className={classNames.join(" ")}
                columns={columns}
                dataSource={dataSource}
                bordered
                title={fullTitle}
                loading={isFetching} // we don't want the full spinner when it's just reloading data
                // TablePaginationPosition is missing "none" although it's in the docs
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                pagination={{ hideOnSinglePage: true, position: ["none"] as any, ...pagination }}
                {...otherProps}
            />
        );
    }
}

export default DynamicTable;
